import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import DOMPurify from "dompurify";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Central API base resolver with safe fallback:
// - Prefer VITE_API_BASE_URL when set
// - But if it points to localhost and we're NOT on localhost, fall back to same-origin /api
// - Otherwise default to same-origin /api
export function resolveApiBase() {
  let api = import.meta.env.VITE_API_BASE_URL;
  try {
    if (!api) {
      if (typeof window !== 'undefined' && window.location?.origin) {
        api = `${window.location.origin}/api`;
      } else {
        api = '/api';
      }
    }
    // If built with localhost API base but running on a real host, prefer same-origin /api
    if (
      api &&
      /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(api) &&
      typeof window !== 'undefined' &&
      window.location?.hostname &&
      !/(^localhost$)|(^127\.)/i.test(window.location.hostname)
    ) {
      api = `${window.location.origin}/api`;
    }
    return api.replace(/\/$/, '');
  } catch (_) {
    return '/api';
  }
}

// Build absolute URL to backend for relative file paths like "/files/..."
export function toBackendUrl(u) {
  if (!u) return "";
  if (typeof u !== "string") return "";
  if (/^https?:\/\//i.test(u)) return u;
  // In dev, prefer relative /files so Vite proxy handles it and avoids CORP
  if (import.meta.env.DEV && u.startsWith("/files/")) return u;
  try {
    const origin = new URL(resolveApiBase()).origin;
    return origin + u;
  } catch (_) {
    return u;
  }
}

// Extract redeem code from a full URL or raw input
export function normalizeRedeemCode(input) {
  const s = String(input || '').trim();
  if (!s) return s;
  try {
    const u = new URL(s);
    const parts = u.pathname.split('/').filter(Boolean);
    // Prefer segment after 'claim' or 'redeem'
    let idx = parts.lastIndexOf('claim');
    if (idx === -1) idx = parts.lastIndexOf('redeem');
    if (idx !== -1 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
    if (parts.length > 0) return decodeURIComponent(parts[parts.length - 1]);
  } catch (_) {
    const m = s.match(/embed\/(?:claim|redeem)\/([^\/?#]+)/i) || s.match(/redeem\/([^\/?#]+)/i);
    if (m) return decodeURIComponent(m[1]);
  }
  return s;
}

// Very small, conservative HTML sanitizer for displaying admin-provided content
// Allows a subset of tags and prunes dangerous attributes and protocols
export function sanitizeHtml(unsafe) {
  try {
    // Use DOMPurify where available for robust sanitization and a conservative policy
    const allowedTags = ['a','b','strong','em','i','u','br','p','ul','ol','li','div','span','h1','h2','h3','h4','h5','h6','small','address','img'];
    const allowedAttrs = ['href','title','target','rel','src','alt'];
    const clean = DOMPurify.sanitize(String(unsafe || ''), {
      ALLOWED_TAGS: allowedTags,
      ALLOWED_ATTR: allowedAttrs,
      FORBID_ATTR: ['style', 'onerror', 'onclick', 'onload'],
    });
    // Ensure links that open in new tab have noopener
    const tmp = document.createElement('div');
    tmp.innerHTML = clean;
    tmp.querySelectorAll('a[target="_blank"]').forEach(a => {
      if (!a.getAttribute('rel')) a.setAttribute('rel','noopener noreferrer');
    });
    return tmp.innerHTML;
  } catch (_) {
    return '';
  }
}

// Minimal Markdown -> HTML converter (headings, bold, italic, links, lists, paragraphs)
export function markdownToHtml(md) {
  if (!md) return '';
  const esc = (s) => s.replace(/[&<>]/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c]));
  const lines = String(md).replace(/\r\n?/g,'\n').split('\n');
  const out = [];
  let inList = false;
  const flushP = (buf) => {
    if (!buf.length) return;
    out.push('<p>' + buf.join(' ') + '</p>');
    buf.length = 0;
  };
  let pbuf = [];
  for (let raw of lines) {
    const line = raw.trimEnd();
    // Headings
    const m = line.match(/^(#{1,6})\s+(.*)$/);
    if (m) {
      flushP(pbuf);
      if (inList) { out.push('</ul>'); inList = false; }
      const lvl = m[1].length;
      const content = m[2];
      out.push(`<h${lvl}>${inline(content)}</h${lvl}>`);
      continue;
    }
    // List items
    if (/^[-*]\s+/.test(line)) {
      flushP(pbuf);
      if (!inList) { out.push('<ul>'); inList = true; }
      const item = line.replace(/^[-*]\s+/, '');
      out.push('<li>' + inline(item) + '</li>');
      continue;
    }
    // Blank line -> paragraph break
    if (line.trim() === '') {
      flushP(pbuf);
      if (inList) { out.push('</ul>'); inList = false; }
      continue;
    }
    pbuf.push(inline(line));
  }
  flushP(pbuf);
  if (inList) out.push('</ul>');
  return out.join('\n');

  function inline(s) {
    // Links [text](url)
    s = s.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, (__, text, url) => {
      const safe = /^https?:|^mailto:/i.test(url) ? url : '#';
      return `<a href="${esc(safe)}" target="_blank" rel="noopener noreferrer">${esc(text)}</a>`;
    });
    // Bold **text**
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Italic *text*
    s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    return s;
  }
}
